import ArrowForward from "@mui/icons-material/ArrowForward";
import Edit from "@mui/icons-material/Edit";
import ExpandMore from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Person from "@mui/icons-material/Person";
import PersonAddAlt1 from "@mui/icons-material/PersonAddAlt1";
import PersonRemove from "@mui/icons-material/PersonRemove";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import { UserSelectDialog } from "components/UserSelectDialog";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { WorkflowDueType } from "generated-graphql/graphql";
import { useUpdateActivityAssignees } from "hooks/activities";
import { useCurrentUser } from "hooks/useCurrentUser";
import { DateTime } from "luxon";
import { MouseEvent, ReactNode, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { formatEstimate, getRowActionLabel, getRowLink } from "./columns";
import { activityStatusColor, workflowStatusColor } from "./statusChipColors";
import { assignBlockedReason, ORIGIN_OWNS_ASSIGNMENT, UnifiedWorkRow } from "./types";
import { useRefetchWorkQueue } from "./useRefetchWorkQueue";
import { WorkDrawerComments } from "./WorkDrawerComments";
import { WorkEstimateDialog } from "./WorkEstimateDialog";
import { WorkTagsDialog } from "./WorkTagsDialog";

const EMPTY = "—";

/**
 * The one distinction on this panel staff must not misread: a regulatory date
 * is a fact about the world, an internal one is a promise we made. Spelled out
 * inline rather than hidden in a tooltip.
 */
const DUE_TYPE_MEANING: Record<WorkflowDueType, string> = {
  [WorkflowDueType.Regulatory]:
    "Immovable statutory filing date — the system will never reschedule it.",
  [WorkflowDueType.Internal]:
    "Internal commitment Encamp set for itself or the customer — renegotiable.",
};

const DUE_TYPE_COLOR: Record<WorkflowDueType, StatusChipColor> = {
  [WorkflowDueType.Regulatory]: "warning",
  [WorkflowDueType.Internal]: "info",
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Box>{children}</Box>
    </Stack>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 130, flexShrink: 0, pt: 0.25 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

function TextFact({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <Fact label={label}>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {value === null || value === undefined || value === "" ? EMPTY : value}
      </Typography>
    </Fact>
  );
}

function IdFact({ label, value }: { label: string; value?: string | null }) {
  return (
    <Fact label={label}>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
        {value ?? EMPTY}
      </Typography>
    </Fact>
  );
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return EMPTY;
  }
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATETIME_MED) : EMPTY;
}

function DueFact({ row }: { row: UnifiedWorkRow }) {
  if (!row.dueAt) {
    return (
      <Fact label="Due">
        <Typography variant="body2">No due date on the workflow</Typography>
      </Fact>
    );
  }
  const due = DateTime.fromISO(row.dueAt);
  const overdue = due.isValid && due < DateTime.now();
  return (
    <Fact label="Due">
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color={overdue ? "error" : undefined}>
            {due.isValid ? due.toLocaleString(DateTime.DATE_MED) : EMPTY}
          </Typography>
          {row.dueType && (
            <StatusChip
              variant="outlined"
              color={DUE_TYPE_COLOR[row.dueType]}
              label={prettyPrintEnumValue(row.dueType)}
            />
          )}
          {overdue && (
            <Typography variant="caption" color="error">
              Overdue
            </Typography>
          )}
        </Stack>
        {row.dueType && (
          <Typography variant="caption" color="text.secondary">
            {DUE_TYPE_MEANING[row.dueType]}
          </Typography>
        )}
      </Stack>
    </Fact>
  );
}

/**
 * The work type's SOP in Academy, when one has been written. Most work types
 * have no 1:1 article yet — the Academy is organized by program and
 * jurisdiction, not by work type — so the row is omitted entirely rather than
 * shown empty: a "—" here would read as a broken link, not an absent one.
 */
function AcademyArticleFact({ row }: { row: UnifiedWorkRow }) {
  if (!row.academyArticleUrl) {
    return null;
  }
  return (
    <Fact label="SOP article">
      <Typography variant="body2">
        <MuiLink
          href={row.academyArticleUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open the Academy SOP article (opens in a new tab)"
        >
          Academy article
          <OpenInNew sx={{ ml: 0.5, fontSize: "0.9em", verticalAlign: "text-bottom" }} />
        </MuiLink>
      </Typography>
    </Fact>
  );
}

function CurrentStepFact({ row }: { row: UnifiedWorkRow }) {
  const stepName = row.activityDescription ?? prettyPrintEnumValue(row.activityType);
  return (
    <Fact label="Current step">
      <Stack spacing={0.5}>
        <Typography variant="body2">
          {stepName === "" ? "No activity running — the workflow is waiting" : stepName}
        </Typography>
        {row.activityStatus && (
          <Box>
            <StatusChip
              color={activityStatusColor(row.activityStatus)}
              label={prettyPrintEnumValue(row.activityStatus)}
            />
          </Box>
        )}
      </Stack>
    </Fact>
  );
}

/**
 * Labels, editable in place through the same dialog Bulk Actions raises with a
 * one-row selection — so the recent-tags list and the confirmation step are the
 * same whichever surface staff came from.
 */
function TagsFact({ row }: { row: UnifiedWorkRow }) {
  const refetchQueue = useRefetchWorkQueue();
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);
  // The dialog re-seeds from `rows` whenever the identity changes; keep it stable.
  const rows = useMemo(() => [row], [row]);

  return (
    <Fact label="Tags">
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {row.tags.length === 0 ? (
          <Typography variant="body2">Untagged</Typography>
        ) : (
          row.tags.map((tag) => <Chip key={tag} label={tag} size="small" />)
        )}
        <Tooltip title="Manage tags">
          <IconButton size="small" aria-label="Edit tags" onClick={openDialog}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <WorkTagsDialog rows={rows} open={dialogOpen} onClose={closeDialog} onSaved={refetchQueue} />
    </Fact>
  );
}

type AssigneeMenuProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  busy: boolean;
  canAssignToMe: boolean;
  assigned: boolean;
  onAssignToMe: () => void;
  onChooseStaff: () => void;
  onUnassign: () => void;
};

/**
 * The same three moves Bulk Actions offers, scoped to this one row. Unassign is
 * absent when there is nothing to take away.
 */
function AssigneeMenu({
  anchorEl,
  onClose,
  busy,
  canAssignToMe,
  assigned,
  onAssignToMe,
  onChooseStaff,
  onUnassign,
}: AssigneeMenuProps) {
  return (
    <Menu
      id="work-drawer-assignee-menu"
      anchorEl={anchorEl}
      open={anchorEl !== null}
      onClose={onClose}
      // The queue grid stays mounted behind the drawer; the padding MUI's body
      // scroll lock adds would reflow it and trip its ResizeObserver.
      disableScrollLock
      MenuListProps={{ "aria-labelledby": "work-drawer-assignee-button" }}
    >
      <MenuItem disabled={busy || !canAssignToMe} onClick={onAssignToMe}>
        <ListItemIcon>
          <Person fontSize="small" />
        </ListItemIcon>
        <ListItemText>Assign to me</ListItemText>
      </MenuItem>
      <MenuItem disabled={busy} onClick={onChooseStaff}>
        <ListItemIcon>
          <PersonAddAlt1 fontSize="small" />
        </ListItemIcon>
        <ListItemText>Choose staff member…</ListItemText>
      </MenuItem>
      {assigned && (
        <MenuItem disabled={busy} onClick={onUnassign}>
          <ListItemIcon>
            <PersonRemove fontSize="small" />
          </ListItemIcon>
          <ListItemText>Unassign</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
}

/**
 * Ownership, editable in place. The write goes to the row's *current activity*
 * (that is where assignment lives), so a workflow with no current activity has
 * nothing to assign and says so instead of failing on click.
 */
function AssigneeFact({ row }: { row: UnifiedWorkRow }) {
  const { user } = useCurrentUser();
  const refetchQueue = useRefetchWorkQueue();
  const [updateAssignees, { loading: assigning }] = useUpdateActivityAssignees(refetchQueue);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  const { activityId } = row;
  const label = row.assigneeEmail ?? "Unassigned";

  const openMenu = useCallback(
    (event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget),
    []
  );
  const closeMenu = useCallback(() => setMenuAnchor(null), []);
  const closeChooser = useCallback(() => setChooserOpen(false), []);

  const applyAssignee = useCallback(
    async (assigneeId: string | null) => {
      if (!activityId) {
        return;
      }
      await updateAssignees({ variables: { ids: [activityId], assigneeId } });
    },
    [activityId, updateAssignees]
  );

  const handleAssignToMe = useCallback(() => {
    closeMenu();
    if (user) {
      void applyAssignee(user.id);
    }
  }, [applyAssignee, closeMenu, user]);

  const handleChooseStaff = useCallback(() => {
    closeMenu();
    setChooserOpen(true);
  }, [closeMenu]);

  const handleUnassign = useCallback(() => {
    closeMenu();
    void applyAssignee(null);
  }, [applyAssignee, closeMenu]);

  const handleSelectUser = useCallback(
    async (userId: string) => {
      await applyAssignee(userId);
      setChooserOpen(false);
    },
    [applyAssignee]
  );

  const blockedReason = assignBlockedReason(row);

  return (
    <Fact label="Assignee">
      <Stack spacing={0.5} alignItems="flex-start">
        {/* A disabled MUI button swallows pointer events, so the tooltip needs the
            span to hang off of. */}
        <Tooltip title={blockedReason ?? ""}>
          <span>
            <Button
              id="work-drawer-assignee-button"
              size="small"
              color="inherit"
              aria-controls={menuAnchor ? "work-drawer-assignee-menu" : undefined}
              aria-haspopup="true"
              aria-label={`Change assignee: ${label}`}
              disabled={blockedReason !== null || assigning}
              endIcon={<ExpandMore />}
              onClick={openMenu}
              sx={{ textTransform: "none", px: 0.5, minWidth: 0 }}
            >
              {label}
            </Button>
          </span>
        </Tooltip>
        {row.assigneeReadOnly && (
          <Typography variant="caption" color="text.secondary">
            {ORIGIN_OWNS_ASSIGNMENT}
          </Typography>
        )}
      </Stack>
      <AssigneeMenu
        anchorEl={menuAnchor}
        onClose={closeMenu}
        busy={assigning}
        canAssignToMe={Boolean(user)}
        assigned={Boolean(row.assigneeId)}
        onAssignToMe={handleAssignToMe}
        onChooseStaff={handleChooseStaff}
        onUnassign={handleUnassign}
      />
      {/* Mounted on demand: the dialog fetches the staff list on mount, and the
          drawer opens far more often than anyone reassigns from it. */}
      {chooserOpen && (
        <UserSelectDialog
          open
          title="Assign work to"
          onClose={closeChooser}
          onSelect={handleSelectUser}
        />
      )}
    </Fact>
  );
}

/**
 * The estimate with an edit affordance. Deliberately the same dialog Bulk
 * Actions raises, handed a one-row selection, so presets, clearing and the
 * success copy stay identical whichever surface staff came from.
 */
function EstimateFact({ row }: { row: UnifiedWorkRow }) {
  const refetchQueue = useRefetchWorkQueue();
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);
  // The dialog re-seeds from `rows` whenever the identity changes; keep it stable.
  const rows = useMemo(() => [row], [row]);

  return (
    <Fact label="Estimate">
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="body2">{formatEstimate(row.estimateMinutes)}</Typography>
        <Tooltip title="Set estimate">
          <IconButton size="small" aria-label="Edit estimate" onClick={openDialog}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <WorkEstimateDialog
        rows={rows}
        open={dialogOpen}
        onClose={closeDialog}
        onSaved={refetchQueue}
      />
    </Fact>
  );
}

function WorkDrawerBody({ row }: { row: UnifiedWorkRow }) {
  return (
    <Stack spacing={2} divider={<Divider flexItem />}>
      <Stack spacing={1}>
        <Typography variant="subtitle1">{row.title ?? EMPTY}</Typography>
        <Typography variant="caption" color="text.secondary">
          Everything below is what the Workflow record holds about this work. Owner, estimate and
          tags are editable here; doing the work itself happens on the linked page.
        </Typography>
      </Stack>

      <Section title="Workflow">
        <TextFact
          label="Work type"
          value={row.workTypeLabel ?? prettyPrintEnumValue(row.workTypeKey)}
        />
        <AcademyArticleFact row={row} />
        <TextFact label="Subject type" value={row.subjectType} />
        <Fact label="Status">
          <StatusChip
            color={workflowStatusColor(row.workflowStatus)}
            label={prettyPrintEnumValue(row.workflowStatus)}
          />
        </Fact>
        <CurrentStepFact row={row} />
      </Section>

      <Section title="Deadline & effort">
        <DueFact row={row} />
        <EstimateFact row={row} />
      </Section>

      <Section title="Ownership">
        <AssigneeFact row={row} />
        <TagsFact row={row} />
      </Section>

      <Section title="Scope">
        <TextFact label="Tenant" value={row.tenantName} />
        <TextFact label="Facility" value={row.facilityName} />
        <TextFact label="State" value={row.state} />
        <TextFact label="Reporting year" value={row.reportingYear} />
      </Section>

      <Section title="Bookkeeping">
        <TextFact label="Created" value={formatDateTime(row.createdAt)} />
        <IdFact label="Workflow ID" value={row.workflowId} />
        <IdFact label="Activity ID" value={row.activityId} />
      </Section>

      <Section title="Comments">
        <WorkDrawerComments workflowId={row.workflowId} />
      </Section>
    </Stack>
  );
}

function WorkDrawerFooter({ row }: { row: UnifiedWorkRow }) {
  const link = getRowLink(row);
  if (!link) {
    return (
      <Typography variant="body2" color="text.secondary">
        This work type has no action surface yet.
      </Typography>
    );
  }
  // The drawer is bookkeeping; the linked page is where work gets done, so the
  // whole footer is one unmissable target.
  return (
    <Button fullWidth variant="contained" component={Link} to={link} endIcon={<ArrowForward />}>
      {getRowActionLabel(row)}
    </Button>
  );
}

export type WorkDrawerProps = {
  /** `?workflowId=` value (see `useWorkDrawer`) — non-null means the drawer is open. */
  workflowId: string | null;
  /**
   * The row the grid already loaded for {@link workflowId}, or null when the
   * deep link points outside the current page/filters. Every fact the drawer
   * shows comes from this row — nothing is re-fetched for data the grid has in
   * hand. The one exception is the comment thread, which the grid never loads
   * and which fetches when the drawer opens.
   */
  row: UnifiedWorkRow | null;
  onClose: () => void;
};

/**
 * Right-hand inspector for one row of the unified work queue: the Workflow
 * wrapper's own bookkeeping — status, current step, due date and what kind of
 * date it is, estimate, owner, tags, scope — plus the work item's comment
 * thread, with a single prominent link to the page where the work is actually
 * done. Owner, estimate and tags are editable in place; the comments are the
 * only thing fetched on open, since the grid row carries everything else.
 * Nothing about the subject itself lives here; that's the linked page's job.
 */
export function WorkDrawer({ workflowId, row, onClose }: WorkDrawerProps) {
  const open = workflowId !== null;
  const title = row ? (row.workTypeLabel ?? prettyPrintEnumValue(row.workTypeKey)) : "Work item";
  return (
    <RightDrawerShell
      open={open}
      onClose={onClose}
      title={title}
      footer={row ? <WorkDrawerFooter row={row} /> : undefined}
      // The drawer paper is kept mounted across open/close; emptying it while
      // closed keeps the panel's text out of the page for screen readers and
      // queries.
      showContent={open}
      // Modal (the shell's default): the drawer is where staff edit the
      // workflow and comment on it, so the queue behind it is inert until they
      // finish — a stray click on another row can't retarget the panel out from
      // under a half-typed comment. Backdrop click, Esc and the X all close.
    >
      {row ? (
        <WorkDrawerBody row={row} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          This work item isn&apos;t on the current page of results. Clear the filters or search for
          it, then open it again.
        </Typography>
      )}
    </RightDrawerShell>
  );
}
